import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { SparkleGlyph, SparklesLoader } from './loading/SparklesLoader';
import { DEFAULT_LOADING_TIPS, type LoadingTip } from './loading/loadingTips';
import {
  LOADING_CAROUSEL_IMAGES,
  generacionMuestraFotos,
  registrarGeneracion,
} from './loading/loadingCarousel';
import { useCompleteSound } from './loading/useCompleteSound';
import { useGenerationProgress } from './loading/useGenerationProgress';

/**
 * Pantalla de carga de **toda** generación de la app (imágenes, videos, upscale,
 * anuncios, logo…) y también la del onboarding nuevo
 * (ADR-2026-08-02-pantalla-carga-unificada).
 *
 * Antes eran dos diseños distintos: un overlay blanco con un Lottie remoto acá, y
 * la pantalla azul con sparkles + carrusel que se hizo para el onboarding
 * (ADR-724/739/743). Ganó el segundo y quedó **uno solo**: el layout interno es
 * idéntico en los dos contextos y `variant` sólo cambia el marco y la entrada.
 *
 * - `variant="app"` (default): overlay sobre la app — backdrop oscuro + blur en
 *   desktop, entrada deslizando desde la derecha en mobile. Los 17 call-sites
 *   existentes no cambiaron una sola prop.
 * - `variant="onboarding"`: pantalla completa sobre gris, con la entrada escalonada
 *   (primero el fondo, después el contenido) que definió ADR-739/743.
 *
 * El marquee de abajo es el mismo slot con dos contenidos: **tarjetas de consejo**
 * en la app (`tips`) y **fotos de ejemplo** en el onboarding (`carouselImages`).
 */

export interface LoadingAnimationProps {
  // ── Contenido ──
  /** Título grande. */
  message?: string;
  /** Línea de apoyo debajo del título. */
  subMessage?: string;
  /** Tarjetas de consejo del marquee. Se ignoran si al final ganan las fotos. */
  tips?: LoadingTip[];
  /**
   * Fuerza las fotos del marquee, sin importar cuántas generaciones lleve el usuario.
   * Lo usa el alta, que está diseñada con fotos siempre. Sin esto, el contenido lo
   * decide la cuenta de generaciones (ver `contenidoDelMarquee` más abajo).
   */
  carouselImages?: string[];
  /**
   * Cuenta este montaje para la cuota de "primeras generaciones con fotos"
   * (ADR-2026-08-04-fotos-en-las-primeras-generaciones). Apagalo cuando el overlay
   * NO representa una generación — ej. cargar un video que ya existe — para no
   * consumirle al usuario un slot de fotos que no usó.
   */
  cuentaComoGeneracion?: boolean;
  /** Título arriba del marquee. Por defecto, el que corresponda al contenido. */
  carouselTitle?: string;

  // ── Progreso ──
  /** Cuánto se estima que tarda la generación (ms). Ver `useGenerationProgress`. */
  durationMs?: number;
  /** Tiempo con la barra quieta en 0% antes de arrancar (ms). */
  holdMs?: number;
  /** Sin duración conocida: barra indeterminada (un segmento que barre en loop). */
  indeterminate?: boolean;
  /** La generación terminó de verdad → la barra salta a 100% y suena el "listo". */
  completeNow?: boolean;
  /** La barra llega sola a 100% al cumplirse `durationMs` (sin el tope de 97%). */
  completeAtEnd?: boolean;
  /** Se llama una vez cuando la barra llega a 100%. */
  onCompleted?: () => void;
  /** Muestra el tiempo aproximado restante (generaciones largas). */
  showCountdown?: boolean;
  /** Texto del countdown cuando llega a 0. */
  countdownDoneText?: string;

  // ── Marco y ciclo de vida ──
  /** Contexto de uso: overlay sobre la app, o pantalla del onboarding. */
  variant?: 'app' | 'onboarding';
  /** El padre pide la salida → fade-out y después `onExitComplete`. */
  exiting?: boolean;
  /** Se llama al terminar el fade-out (el padre navega acá). */
  onExitComplete?: () => void;
}

/** Duración del fade-out, igual en las dos variantes (ms). */
const EXIT_MS = 300;

/**
 * En el onboarding la ilustración arranca quieta y la animación empieza recién
 * después de la entrada escalonada, para que no se vea moverse mientras aparece
 * (ADR-744). En la app la entrada dura 300ms, así que anima desde el principio.
 */
const ONBOARDING_SPARKLES_DELAY_MS = 1300;

const CSS = `
@keyframes atlas-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
@keyframes atlas-indet { 0% { margin-left: -40%; } 100% { margin-left: 100%; } }
@media (prefers-reduced-motion: reduce) {
  .atlas-marquee-track { animation: none !important; }
  .atlas-progress-indet { animation: none !important; margin-left: 0 !important; width: 100% !important; }
}
`;

// ── Carrusel de consejos: avanza de a pasos, no en continuo ──
// Las fotos del onboarding pueden desfilar sin parar (se miran de reojo), pero un
// consejo hay que **leerlo**: cada tarjeta se queda quieta y recién después avanza
// una posición, con un easing suave.
/** Cuánto se queda quieta cada tarjeta antes de avanzar (ms). */
const TIP_HOLD_MS = 4000;
/** Cuánto dura el avance en sí (ms). */
const TIP_MOVE_MS = 800;

// Medidas de la tarjeta. Están en px (y no en clases tipo `mr-3`) porque los keyframes
// se calculan con estos números: si el JSX y estas constantes se separan, el carrusel
// avanza mal. Cambiar una = cambiar la otra.
/** Ancho de la tarjeta. Debe coincidir con `w-[280px]` del JSX. */
const TIP_CARD_W = 280;
/** Separación entre tarjetas. Debe coincidir con `mr-[12px]` del JSX. */
const TIP_CARD_GAP = 12;
/** Cuánto avanza el carrusel en cada paso: una tarjeta entera. */
const TIP_STEP = TIP_CARD_W + TIP_CARD_GAP;
/**
 * Corrimiento base, para que quede una tarjeta **centrada** y no pegada al borde.
 * Sin él, el centro del contenedor cae sobre el BORDE de una tarjeta y se veía una
 * entera a la izquierda con la siguiente cortada de golpe; corriendo media tarjeta,
 * las vecinas asoman parejo a los dos lados.
 */
const TIP_BASE_OFFSET = TIP_CARD_W / 2;

/**
 * Cuántas copias de la lista lleva la pista. **Tres, y el recorrido va por la del
 * MEDIO** (ADR-2026-08-07-marquee-de-consejos-recorre-la-copia-del-medio).
 *
 * Con dos copias el loop se cortaba: el contenedor centra la pista
 * (`justify-center`), así que el centro del viewport cae en la MITAD de la pista —
 * o sea, en la juntura entre las dos copias— y el recorrido terminaba caminando la
 * copia 2. Al llegar a su última tarjeta no quedaba nada a la derecha (era la última
 * de la pista) y el fotograma de cierre aterrizaba una tarjeta más allá del final, en
 * el vacío, antes de saltar de vuelta al principio. Justo lo reportado: "no tiene
 * nada a su derecha y después de la nada aparece".
 *
 * Con tres copias y el recorrido por la del medio siempre hay una copia entera de
 * relleno a cada lado, y el cierre cae sobre la primera tarjeta de la copia 3 — que
 * es idéntica a donde arranca el ciclo, así que el salto de vuelta no se ve.
 */
const TIP_COPIAS = 3;

/**
 * Arma los `@keyframes` del carrusel de consejos para `pasos` tarjetas.
 *
 * Se genera en runtime porque depende de cuántos consejos haya: no se puede
 * escribir a mano una sola animación que sirva para 3, 4 o 7 tarjetas.
 *
 * Se mueve en **px** y no en %, porque el corrimiento que centra la tarjeta
 * (`TIP_BASE_OFFSET`) es media tarjeta — un valor fijo que no se puede expresar como
 * porcentaje de una pista cuyo largo depende de cuántos consejos haya.
 *
 * Entre paso y paso hay un keyframe repetido: eso produce la pausa (dos keyframes con
 * el mismo `transform`), y el movimiento ocurre entre el último keyframe de un paso y
 * el primero del siguiente.
 */
function tipsKeyframes(pasos: number): { nombre: string; css: string; duracionMs: number } {
  const cicloMs = TIP_HOLD_MS + TIP_MOVE_MS;
  const duracionMs = pasos * cicloMs;
  const pct = (ms: number) => ((ms / duracionMs) * 100).toFixed(4);

  // Desplazamiento que deja centrada la tarjeta `i` de la copia del MEDIO.
  //
  // La pista está centrada, así que el centro del contenedor cae en `pasos*COPIAS/2`
  // tarjetas contadas desde el arranque de la pista. Para mostrar la tarjeta
  // `pasos + i` (la `i` de la copia 2 de 3) hay que correr esa diferencia. Con 3
  // copias da `i - pasos/2`, que para el primer paso es NEGATIVO: la pista arranca
  // corrida hacia la derecha y de ahí camina hacia la izquierda. Es correcto —
  // `translateX` acepta los dos signos.
  const desplazamiento = (i: number) =>
    TIP_BASE_OFFSET + (i - (pasos * (TIP_COPIAS - 2)) / 2) * TIP_STEP;

  const frames: string[] = [];
  for (let i = 0; i < pasos; i++) {
    const x = desplazamiento(i);
    // Llega a la posición…
    frames.push(`${pct(i * cicloMs)}% { transform: translateX(${-x}px); }`);
    // …y se queda ahí hasta que termina el hold.
    frames.push(`${pct(i * cicloMs + TIP_HOLD_MS)}% { transform: translateX(${-x}px); }`);
  }
  // Cierre: una copia entera más allá del arranque. Es la misma imagen que el 0%
  // (misma tarjeta, mismas vecinas), así que el reinicio del loop no se percibe.
  frames.push(`100% { transform: translateX(${-desplazamiento(pasos)}px); }`);

  const nombre = `atlas-tips-${pasos}`;
  return { nombre, css: `@keyframes ${nombre} { ${frames.join(' ')} }`, duracionMs };
}

const LoadingAnimation: React.FC<LoadingAnimationProps> = ({
  message = 'Generando imágenes...',
  subMessage,
  tips = DEFAULT_LOADING_TIPS,
  carouselImages,
  cuentaComoGeneracion = true,
  carouselTitle,
  durationMs = 30000,
  holdMs = 0,
  indeterminate = false,
  completeNow = false,
  completeAtEnd = false,
  onCompleted,
  showCountdown = false,
  countdownDoneText,
  variant = 'app',
  exiting = false,
  onExitComplete,
}) => {
  const esOnboarding = variant === 'onboarding';

  // Animaciones de entrada/salida.
  const [isEntering, setIsEntering] = useState<boolean>(true);
  const [isExiting, setIsExiting] = useState<boolean>(false);

  // El sonido sólo existe en la app. En el onboarding ni se inicializa el audio.
  const playCompleteSound = useCompleteSound(!esOnboarding);

  const progress = useGenerationProgress({
    durationMs,
    holdMs,
    indeterminate,
    completeNow,
    completeAtEnd,
    onDone: (forzado) => {
      // Suena sólo cuando terminó el trabajo real. Que se cumpla el timer del
      // ejemplo precargado (`completeAtEnd`) no es una completitud: no suena.
      if (forzado) playCompleteSound();
      onCompleted?.();
    },
  });

  const [remainingSeconds, setRemainingSeconds] = useState<number>(Math.ceil(durationMs / 1000));

  // Fade-in al montar: se pinta el primer frame en el estado "entrando" y en el
  // siguiente se saca, así el navegador tiene de dónde animar.
  useEffect(() => {
    const timer = requestAnimationFrame(() => setIsEntering(false));
    return () => cancelAnimationFrame(timer);
  }, []);

  // Salida: el padre levanta `exiting`, se hace el fade y recién ahí navega.
  //
  // El guard va en un REF y las deps son sólo `[exiting]` — a propósito. La versión
  // anterior usaba `if (exiting && !isExiting)` con deps `[exiting, isExiting,
  // onExitComplete]` y **nunca disparaba `onExitComplete`**: el `setIsExiting(true)`
  // de adentro cambiaba una dep, así que React limpiaba el efecto (→ `clearTimeout`)
  // un frame después de armarlo y al re-correr caía por el early-return sin volver a
  // armarlo. Quedó tapado porque los dos call-sites que la usan esconden la pantalla
  // por otros caminos (`setShowLoadingScreen(false)` suelto); acá no se puede tapar,
  // porque el onboarding navega desde este callback.
  const exitStartedRef = useRef<boolean>(false);
  const onExitCompleteRef = useRef<(() => void) | undefined>(onExitComplete);
  onExitCompleteRef.current = onExitComplete;

  useEffect(() => {
    if (!exiting || exitStartedRef.current) return;
    exitStartedRef.current = true;
    setIsExiting(true);
    playCompleteSound();
    const timer = setTimeout(() => onExitCompleteRef.current?.(), EXIT_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exiting]);

  // Countdown para generaciones largas (video).
  useEffect(() => {
    if (!showCountdown) return;
    const interval = setInterval(() => {
      setRemainingSeconds((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [showCountdown]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // ── Clases del marco, por variante ──
  // Escritas literales (nada de interpolar en el nombre de la clase) porque
  // Tailwind escanea el código fuente como texto: una clase armada en runtime
  // no se compila y queda sin estilo.
  const backdropBase = 'fixed inset-0 z-[100000] pwa-screen flex items-center justify-center';

  // App: backdrop oscuro + blur en desktop; en mobile la tarjeta tapa todo y entra
  // deslizando desde la derecha. El color de desktop va en hex con alpha (`#02062B` =
  // `--brand-black`) porque `bg-brand-black/50` NO compila: los colores de marca
  // son `var(--…)` y Tailwind no les puede inyectar opacidad — la clase no existía
  // en el CSS y el backdrop se veía borroso pero sin oscurecer.
  //
  // El `bg-[#3f5bd6]` de mobile es el MISMO azul de la tarjeta y está para iOS
  // (ADR-2026-08-04-fondo-azul-hasta-el-borde-en-ios). Safari usa lo que hay pintado en
  // el borde del documento para teñir su barra inferior y el overscroll (ver el
  // comentario de `html` en `index.css`). La tarjeta mide `--app-height`, que **excluye**
  // la barra del navegador, así que sin esto quedaba una franja blanca justo ahí y la
  // barra se veía blanca — mientras que en el alta, que sí tiene fondo en esta capa, se
  // teñía de azul. Ahora las dos pantallas se comportan igual.
  const backdropApp = `${backdropBase} bg-[#3f5bd6] transition-all duration-300 ease-out lg:bg-[#02062B80] lg:backdrop-blur-sm lg:p-8 ${
    isEntering
      ? 'lg:opacity-0 translate-x-full lg:translate-x-0'
      : isExiting
        ? 'opacity-0 translate-x-0 pointer-events-none'
        : 'opacity-100 translate-x-0'
  }`;

  // Onboarding: fondo gris en desktop y entrada lenta (el fondo aparece primero y
  // el contenido después, ADR-739/743); la salida queda rápida.
  const backdropOnboarding = `${backdropBase} bg-[#3f5bd6] transition-opacity ease-out lg:bg-[#808080] lg:p-8 ${
    isEntering
      ? 'opacity-0 duration-[250ms]'
      : isExiting
        ? 'opacity-0 duration-[250ms] pointer-events-none'
        : 'opacity-100 duration-[1000ms]'
  }`;

  // El contenido del onboarding entra DESPUÉS del fondo. En la app entra junto.
  const contenidoFade = esOnboarding
    ? `transition-opacity ease-out ${
        isEntering || isExiting
          ? 'opacity-0 duration-[250ms]'
          : 'opacity-100 delay-[600ms] duration-[1000ms]'
      }`
    : '';

  // ── Marquee ──
  // Las primeras `GENERACIONES_CON_FOTOS` van con **fotos** y de ahí en más con
  // **consejos** (ADR-2026-08-04-fotos-en-las-primeras-generaciones). La decisión se
  // congela al montar: si se recalculara en cada render, el marquee podría cambiar de
  // contenido con la pantalla abierta. El inicializador es una lectura pura, así que
  // no importa que StrictMode lo invoque dos veces.
  const [muestraFotos] = useState(generacionMuestraFotos);

  // El registro sí muta, así que va en un efecto y **una sola vez**: en StrictMode los
  // efectos de montaje corren dos veces y sin el ref contaría de a dos (el ref sobrevive
  // al desmontaje simulado, que es justamente para lo que sirve).
  const generacionRegistradaRef = useRef<boolean>(false);
  useEffect(() => {
    // Solo los montajes que representan una GENERACIÓN real avanzan la cuota:
    // los overlays de carga (ej. GeneratedVideoPage cargando un video existente)
    // pasan `cuentaComoGeneracion={false}` y no consumen slots de fotos.
    if (!cuentaComoGeneracion || generacionRegistradaRef.current) return;
    generacionRegistradaRef.current = true;
    registrarGeneracion();
  }, [cuentaComoGeneracion]);

  // `carouselImages` explícito manda sobre la cuenta: es el alta, que va con fotos
  // siempre. Se duplica la lista para el loop (ver `tipsKeyframes` / `atlas-marquee`).
  const fotosForzadas = carouselImages && carouselImages.length > 0 ? carouselImages : null;
  const marqueeFotos = fotosForzadas ?? (muestraFotos ? LOADING_CAROUSEL_IMAGES : null);
  const marqueeTips = marqueeFotos ? null : tips.length > 0 ? tips : null;
  const hayMarquee = Boolean(marqueeFotos || marqueeTips);
  // Fotos: desfile continuo. Consejos: paso a paso, con pausa para leerlos.
  const tipsAnim = marqueeTips ? tipsKeyframes(marqueeTips.length) : null;
  const tituloMarquee =
    carouselTitle ??
    (marqueeFotos ? 'Esto es lo que podés crear con Atlas' : 'Consejos para mejores resultados');

  const Titulo = esOnboarding ? 'h1' : 'h2';

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div className={esOnboarding ? backdropOnboarding : backdropApp}>
      <style>{tipsAnim ? `${CSS}\n${tipsAnim.css}` : CSS}</style>

      {/* Tarjeta: full-bleed en mobile; en desktop (`lg:`) 800px con 16px de radio +
          sombra. `overflow-hidden` recorta la foto de fondo y el marquee. */}
      <div className="pwa-screen-fixed relative flex w-full flex-col overflow-hidden bg-[#3f5bd6] font-sans text-white lg:h-[calc(100dvh_-_64px)] lg:max-h-[640px] lg:min-h-0 lg:w-[800px] lg:max-w-full lg:rounded-[16px] lg:shadow-[0_24px_60px_-12px_rgba(0,0,0,0.35)]">
        <img
          src="/images/onboarding/generation/loading-bg.jpg"
          alt=""
          aria-hidden
          className="pointer-events-none absolute inset-0 size-full object-cover"
        />

        <div
          className={`relative flex h-full flex-col gap-10 px-5 pb-10 pt-[calc(env(safe-area-inset-top)+20px)] lg:min-h-0 lg:gap-8 lg:px-0 lg:pb-8 lg:pt-8 ${contenidoFade}`}
        >
          {/* Bloque central: sparkles + textos + barra. En desktop se acota a 440px. */}
          <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 lg:mx-auto lg:w-full lg:max-w-[440px]">
            <SparklesLoader startDelayMs={esOnboarding ? ONBOARDING_SPARKLES_DELAY_MS : 0} />

            <div className="flex w-full flex-col items-center gap-5">
              <div role="status" aria-live="polite" className="flex flex-col items-center gap-2">
                <Titulo className="text-center text-[26px] font-[600] leading-[31px] text-[#FAFAFA]">
                  {message}
                </Titulo>
                {subMessage && (
                  <p className="text-center text-[16px] font-[500] leading-[21px] text-[#FAFAFA]">
                    {subMessage}
                  </p>
                )}
              </div>

              <div className="flex w-full flex-col items-center gap-4">
                {showCountdown && (
                  <p className="text-center text-[14px] font-[500] leading-[18px] text-white/75">
                    {remainingSeconds > 0 ? (
                      <>
                        Tiempo aprox. restante:{' '}
                        <span className="font-[600] text-[#FAFAFA]">
                          {formatTime(remainingSeconds)}
                        </span>
                      </>
                    ) : (
                      countdownDoneText || 'Tu video estará disponible en breve'
                    )}
                  </p>
                )}

                <div className="h-2 w-full overflow-hidden rounded-full bg-white">
                  {indeterminate ? (
                    // Duración real desconocida: un segmento que barre en loop.
                    <div
                      className="atlas-progress-indet h-full w-2/5 rounded-full bg-[#1644EB]"
                      style={{ animation: 'atlas-indet 1.3s ease-in-out infinite' }}
                    />
                  ) : (
                    // Sin `transition` de CSS: el ancho ya lo escribe el RAF en cada
                    // frame, y una transición encima sólo agregaba ~300ms de lag.
                    <div
                      className="h-full rounded-full bg-[#1644EB]"
                      style={{ width: `${progress}%` }}
                      role="progressbar"
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-valuenow={Math.round(progress)}
                      aria-label="Progreso de generación"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Marquee: consejos (app) o fotos de ejemplo (onboarding) */}
          {hayMarquee && (
            <div className="flex flex-col items-center gap-8">
              <p className="text-center text-[18px] font-[600] leading-[23px] text-[#FAFAFA]">
                {tituloMarquee}
              </p>
              {/* Mobile: full-bleed (`w-screen`). Desktop: el ancho de la tarjeta,
                  de borde a borde; centrado por `items-center`.
                  Con consejos se suma `justify-center`: la pista es más ancha que el
                  contenedor, así que centrarla reparte el sobrante a los dos lados y las
                  tarjetas vecinas asoman parejo (el desfile de fotos no lo necesita — no
                  se detiene nunca en ninguna posición). */}
              <div
                className={`w-screen overflow-hidden md:w-[600px] lg:w-[800px] ${
                  tipsAnim ? 'flex justify-center' : ''
                }`}
              >
                <div
                  className="atlas-marquee-track flex w-max"
                  style={{
                    animation: tipsAnim
                      ? `${tipsAnim.nombre} ${tipsAnim.duracionMs}ms ease-in-out infinite`
                      : 'atlas-marquee 22s linear infinite',
                  }}
                >
                  {marqueeFotos
                    ? [...marqueeFotos, ...marqueeFotos].map((src, i) => (
                        <img
                          key={i}
                          src={src}
                          alt=""
                          aria-hidden
                          draggable={false}
                          className="mr-3 aspect-[2/3] h-[clamp(120px,19dvh,174px)] w-auto shrink-0 rounded-[12px] object-cover"
                        />
                      ))
                    : // `TIP_COPIAS` copias: el recorrido camina la del MEDIO, así que
                      // siempre hay una copia entera de relleno a cada lado y el loop
                      // no se queda sin tarjetas al llegar al final (ver `TIP_COPIAS`).
                      // Sólo la PRIMERA copia queda expuesta a lectores de pantalla:
                      // las de relleno son decorativas.
                      Array.from({ length: TIP_COPIAS }, () => marqueeTips!)
                        .flat()
                        .map((tip, i) => (
                        <div
                          key={i}
                          aria-hidden={i >= marqueeTips!.length}
                          // Ancho y separación en px: los keyframes se calculan con esos
                          // mismos números (`TIP_CARD_W` / `TIP_CARD_GAP`).
                          className="mr-[12px] flex w-[280px] shrink-0 flex-col gap-2 rounded-[12px] border border-white/25 bg-[#FFFFFF1F] p-4 backdrop-blur-sm"
                        >
                          <div className="flex items-center gap-2">
                            <SparkleGlyph className="size-4 text-[#FAFAFA]" />
                            <p className="text-[14px] font-[600] leading-[18px] text-[#FAFAFA]">
                              {tip.title}
                            </p>
                          </div>
                          <p className="text-[13px] font-[400] leading-[18px] text-white/80">
                            {tip.text}
                          </p>
                        </div>
                      ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default LoadingAnimation;
